import React, { useEffect, useRef, useState } from "react";
import {
  Bike,
  ShieldCheck,
  MapPin,
  Headphones,
  ArrowUpRight,
} from "lucide-react";

import directionImage from "../Images/DELHI (DKMR) BG Banner (800x550)_1x.webp";
import Navbar from "./Navbar";
import Footer from "./Footer";
import DownloadApp from "./DownloadApp";
const services = [
  {
    id: 1,
    title: "Food Ordering",
    description:
      "From tandoori platters to a quick roll — order from thousands of restaurants, cloud kitchens and home chefs verified by our team.",
    icon: Bike,
    iconColor: "#ff681d",
    iconBackground: "#fff1e8",
  },
  {
    id: 2,
    title: "Fresh Essentials",
    description:
      "Groceries, fruits, vegetables, bakery and daily essentials from neighbourhood stores you already trust.",
    icon: ShieldCheck,
    iconColor: "#10b981",
    iconBackground: "#e8faf3",
  },
  {
    id: 3,
    title: "WhatsApp Commerce",
    description:
      "Place, track and repeat orders directly on WhatsApp — no new app, no learning curve for customers or shop owners.",
    icon: MapPin,
    iconColor: "#365494",
    iconBackground: "#e8edf8",
  },
  {
    id: 4,
    title: "Everyday Convenience",
    description:
      "One connected experience for meals, milk, mithai or last-minute groceries — all delivered from close to home.",
    icon: Bike,
    iconColor: "#ff681d",
    iconBackground: "#fff1e8",
  },
  {
    id: 5,
    title: "Local Partners",
    description:
      "We onboard local restaurants and shops with fair economics, transparent settlements and dedicated business support.",
    icon: ShieldCheck,
    iconColor: "#10b981",
    iconBackground: "#e8faf3",
  },
  {
    id: 6,
    title: "WhatsApp Ordering",
    description:
      "Customers chat to order, partners receive orders on WhatsApp and dashboard — no missed tickets, no confusion.",
    icon: MapPin,
    iconColor: "#365494",
    iconBackground: "#e8edf8",
  },
];

const timeline = [
  {
    id: 1,
    title: "The idea",
    description:
      "Chaafo begins as a WhatsApp-first ordering tool for a handful of neighbourhood restaurants.",
    iconBackground: "#fff1e8",
  },
  {
    id: 2,
    title: "First 500 partners",
    description:
      "We grow to 500+ local restaurants and grocery stores across 4 cities with same-day onboarding.",
    iconBackground: "#e8faf3",
  },
  {
    id: 3,
    title: "Fresh & essentials go live",
    description:
      "Fresh produce, bakery and daily essentials join the platform with dedicated 20-minute delivery.",
    iconBackground: "#e8edf8",
  },
  {
    id: 4,
    title: "Present day",
    description:
      "10,000+ partners, delivering millions of orders every month across food, grocery and everyday needs.",
    iconBackground: "#e8edf8",
  },
];

const stats = [
  {
    value: 120,
    suffix: "+",
    title: "Partner outlets",
  },
  {
    value: 2,
    suffix: "+",
    title: "Cities Served",
  },
  {
    value: 12,
    suffix: "M+",
    title: "Orders delivered",
  },
  {
    value: 26,
    suffix: " min",
    title: "Avg. delivery time",
  },
];

const cards = [
  {
    id: 1,
    title: "Our Mission",
    description:
      "To make everyday commerce effortless for neighbourhoods – giving local restaurants and stores the same technology and reach as global chains, while giving customers a delightful, on-time experience every single time.",
    icon: Bike,
    iconColor: "#ff681d",
    iconBackground: "#fff1e8",
  },
  {
    id: 2,
    title: "Our Vision",
    description:
      "A world where every local business is discoverable, orderable and lovable – whether the customer prefers a modern app or a simple WhatsApp chat.",
    icon: ShieldCheck,
    iconColor: "#09b981",
    iconBackground: "#e6faf3",
  },
];

const features = [
  {
    id: 1,
    title: "Lightning-fast delivery",
    description:
      "Hot food at your door in 25 minutes or it is on us — guaranteed.",
    icon: Bike,
    iconColor: "#ff681d",
    iconBackground: "#fff1e8",
  },
  {
    id: 2,
    title: "Quality you can trust",
    description:
      "Every partner kitchen is inspected and rated for hygiene and taste.",
    icon: ShieldCheck,
    iconColor: "#09b981",
    iconBackground: "#e6faf3",
  },
  {
    id: 3,
    title: "Live Tracking",
    description: "Know exactly where your order is and when it will arrive.",
    icon: MapPin,
    iconColor: "#365494",
    iconBackground: "#e8edf8",
  },
  {
    id: 4,
    title: "24/7 support",
    description: "Anytime. Chat or call — we have got your back.",
    icon: Headphones,
    iconColor: "#ec62b0",
    iconBackground: "#fde8f5",
  },
];

function AnimatedCounter({ end, duration = 2000, suffix = "" }) {
  const [count, setCount] = useState(0);

  const counterRef = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const element = counterRef.current;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || started.current) return;

        started.current = true;

        let startTime = null;

        const animate = (currentTime) => {
          if (!startTime) startTime = currentTime;

          const progress = Math.min((currentTime - startTime) / duration, 1);

          const value = Math.floor(progress * end);

          setCount(value);

          if (progress < 1) {
            requestAnimationFrame(animate);
          } else {
            setCount(end);
          }
        };

        requestAnimationFrame(animate);
      },
      {
        threshold: 0.4,
      },
    );

    if (element) observer.observe(element);

    return () => observer.disconnect();
  }, [end, duration]);

  return (
    <span ref={counterRef}>
      {count}
      {suffix}
    </span>
  );
}

function WhatWeDo() {
  return (
    <section className="w-full bg-blue-50 px-4 py-12 sm:px-5 md:px-8 md:py-16">
      <div className="mx-auto max-w-[1260px]">
        {/* Heading */}
        <div className="mx-auto max-w-[760px] text-center">
          <span className="inline-flex h-[30px] items-center rounded-full bg-[#2B3F89] px-4 text-[11px] font-medium uppercase tracking-wide text-white sm:h-[32px] sm:px-5 sm:text-[12px]">
            WHAT WE DO
          </span>

          <h2 className="mt-5 text-[28px] font-bold leading-10 tracking-[-0.5px] text-[#4E4E4E] sm:text-[34px] md:text-[40px] lg:text-[36px]">
            One platform for everything your
            <br className="hidden sm:block" />
            neighborhood needs.
          </h2>

          <p className="mx-auto mt-4 max-w-[560px] text-[14px] leading-6 text-[#8FA0B8] sm:text-[15px] md:text-[16px] md:leading-7">
            From restaurants to grocery stores, Chaafo powers the everyday
            commerce of your locality.
          </p>
        </div>

        {/* Cards */}
        <div className="mt-10 grid grid-cols-1 gap-5 sm:mt-12 sm:grid-cols-2 md:gap-6 xl:mt-14 xl:grid-cols-3">
          {services.map((item) => {
            const Icon = item.icon;

            return (
              <article
                key={item.id}
                className="rounded-[18px] bg-white p-5 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg sm:p-6 md:p-7"
              >
                {/* Icon + Title */}
                <div className="flex items-center gap-4">
                  <div
                    className="flex h-[42px] w-[42px] items-center justify-center rounded-xl sm:h-[46px] sm:w-[46px]"
                    style={{
                      backgroundColor: item.iconBackground,
                    }}
                  >
                    <div
                      className="flex h-[30px] w-[30px] items-center justify-center rounded-lg sm:h-[34px] sm:w-[34px]"
                      style={{
                        backgroundColor: item.iconColor,
                      }}
                    >
                      <Icon
                        size={16}
                        strokeWidth={1.8}
                        className="text-white sm:h-[18px] sm:w-[18px]"
                      />
                    </div>
                  </div>

                  <h3 className="text-[20px] font-semibold text-[#505050] sm:text-[22px]">
                    {item.title}
                  </h3>
                </div>

                <p className="mt-3 text-[14px] leading-6 text-[#8FA0B8] sm:text-[15px] sm:leading-7">
                  {item.description}
                </p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function OurDirection() {
  return (
    <section className="w-full bg-blue-50 px-4 py-12 sm:px-5 md:px-8 md:py-16">
      <div className="mx-auto max-w-[1260px]">
        {/* Heading */}
        <div className="text-center">
          <span className="inline-flex h-[30px] items-center rounded-full bg-[#2B3F89] px-4 text-[11px] font-medium uppercase text-white sm:h-8 sm:px-5 sm:text-xs">
            Our Direction
          </span>

          <h2 className="mt-5 text-[28px] font-bold leading-tight text-[#4d4d4d] sm:text-[34px] md:text-[40px] lg:text-4xl">
            Where we've been,
            <br className="sm:hidden" /> and where we're going.
          </h2>
        </div>

        {/* Content */}
        <div className="mt-10 grid grid-cols-1 gap-6 lg:mt-12 lg:grid-cols-[1.1fr_.9fr]">
          {/* Left Image */}
          <div className="overflow-hidden rounded-2xl sm:rounded-3xl">
            <img
              src={directionImage}
              alt="Our Direction"
              className="h-full w-full object-cover"
            />
          </div>

          {/* Right Cards */}
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            {timeline.map((item) => {
              return (
                <div
                  key={item.id}
                  className="rounded-2xl bg-white p-5 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg sm:rounded-3xl sm:p-6"
                >
                  <h3 className="text-[20px] font-semibold text-[#505050] sm:text-[22px]">
                    {item.title}
                  </h3>

                  <p className="mt-3 text-[14px] leading-6 text-[#8FA0B8] sm:text-[15px] sm:leading-7">
                    {item.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

function StatsSection() {
  return (
    <section className="w-full bg-blue-50 px-4 py-12 sm:px-5 md:px-8 md:py-16">
      <div className="mx-auto max-w-[1260px] rounded-[18px] bg-white px-5 py-8 shadow-sm sm:rounded-[22px] sm:px-6 sm:py-10 md:px-8">
        <div className="grid grid-cols-2 gap-y-8 sm:gap-y-10 md:grid-cols-4 md:gap-y-0">
          {stats.map((item, index) => (
            <div
              key={index}
              className="flex flex-col items-center px-2 text-center"
            >
              <h2 className="text-[32px] font-bold leading-none text-[#4E4E4E] sm:text-[38px] md:text-[42px]">
                <AnimatedCounter
                  end={item.value}
                  suffix={item.suffix}
                  duration={2000}
                />
              </h2>

              <p className="mt-3 text-[13px] font-medium leading-5 text-[#93A4BE] sm:mt-4 sm:text-[15px]">
                {item.title}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function MissionVision() {
  return (
    <section className="w-full bg-blue-50 px-4 py-12 sm:px-5 md:px-8 md:py-16">
      <div className="mx-auto max-w-[1260px]">
        {/* Heading */}
        <div className="text-center">
          <span className="inline-flex h-[30px] items-center rounded-full bg-[#2C3E87] px-4 text-[11px] font-medium uppercase tracking-wide text-white sm:h-[32px] sm:px-5 sm:text-[12px]">
            Mission & Vision
          </span>

          <h2 className="mt-5 text-[28px] font-bold leading-tight tracking-[-0.5px] text-[#4E4E4E] sm:text-[34px] md:text-[40px] lg:text-4xl">
            What drives Chaafo every day.
          </h2>
        </div>

        {/* Cards */}
        <div className="mt-10 grid grid-cols-1 gap-5 sm:mt-12 sm:gap-6 lg:grid-cols-2">
          {cards.map((card) => {
            const Icon = card.icon;

            return (
              <article
                key={card.id}
                className="rounded-[18px] bg-white p-5 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg sm:p-6 md:px-7 md:py-7"
              >
                {/* Icon + Title */}
                <div className="flex items-center gap-4">
                  <div
                    className="flex h-[42px] w-[42px] items-center justify-center rounded-xl sm:h-[46px] sm:w-[46px]"
                    style={{
                      backgroundColor: card.iconBackground,
                    }}
                  >
                    <div
                      className="flex h-[30px] w-[30px] items-center justify-center rounded-lg sm:h-[34px] sm:w-[34px]"
                      style={{
                        backgroundColor: card.iconColor,
                      }}
                    >
                      <Icon
                        size={16}
                        strokeWidth={1.8}
                        className="text-white sm:h-[18px] sm:w-[18px]"
                      />
                    </div>
                  </div>

                  <h3 className="text-[20px] font-semibold text-[#505050] sm:text-[20px] md:text-[22px]">
                    {card.title}
                  </h3>
                </div>

                {/* Description */}
                <p className="mt-3 text-[14px] leading-6 text-[#8FA0B8] sm:text-[15px] sm:leading-7">
                  {card.description}
                </p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function BuiltForFood() {
  return (
    <section className="w-full bg-blue-50 px-4 py-[54px] sm:px-5 md:px-8 md:py-[60px]">
      <div className="mx-auto w-full max-w-[1260px]">
        {/* Description */}
        <div className="mx-auto max-w-[620px] text-center">
          <h1 className="text-[30px] font-bold leading-tight tracking-[-0.7px] text-[#1f2937] md:text-[36px]">
            Built for people who love food
          </h1>

          <p className="mx-auto mt-3 max-w-[610px] text-[14px] font-medium leading-[20px] text-gray-500 sm:text-[15px] md:text-[16px] md:leading-[21px]">
            We obsess over every detail so your next meal is effortless, fast,
            and exactly what you craved.
          </p>
        </div>

        {/* Feature Cards */}
        <div className="mt-[46px] grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 lg:gap-6">
          {features.map((feature) => {
            const Icon = feature.icon;

            return (
              <article
                key={feature.id}
                onMouseEnter={(event) => {
                  event.currentTarget.style.borderColor = feature.iconColor;
                }}
                onMouseLeave={(event) => {
                  event.currentTarget.style.borderColor = "transparent";
                }}
                className="min-h-[168px] rounded-[16px] border border-transparent bg-white px-6 pb-5 pt-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_14px_35px_rgba(255,255,255,0.12)]"
              >
                {/* Icon + Title */}
                <div className="flex items-center gap-4">
                  <div
                    className="flex h-[44px] w-[44px] items-center justify-center rounded-[11px]"
                    style={{
                      backgroundColor: feature.iconBackground,
                    }}
                  >
                    <div
                      className="flex h-[34px] w-[34px] items-center justify-center rounded-[8px]"
                      style={{
                        backgroundColor: feature.iconColor,
                      }}
                    >
                      <Icon
                        size={18}
                        strokeWidth={1.8}
                        className="text-white"
                      />
                    </div>
                  </div>

                  <h3 className="text-[18px] font-medium leading-[22px] text-[#505050]">
                    {feature.title}
                  </h3>
                </div>

                <p className="mt-[7px] text-[14px] leading-[21px] text-[#8FA0B8]">
                  {feature.description}
                </p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function PartnerSection() {
  return (
    <section className="w-full bg-blue-50 px-4 py-10 sm:px-5 md:px-8 md:py-14">
      <div className="mx-auto flex max-w-[1330px] flex-col items-center justify-between gap-8 rounded-[18px] bg-white px-5 py-8 shadow-sm sm:px-8 sm:py-10 lg:flex-row lg:px-14">
        {/* Left */}
        <div className="text-center lg:text-left">
          <span className="inline-flex h-[30px] items-center rounded-full bg-[#273B83] px-4 text-[11px] font-medium uppercase tracking-wide text-white sm:h-[32px] sm:px-5 sm:text-[12px]">
            LET'S GROW TOGETHER
          </span>

          <h2 className="mt-5 text-[26px] font-semibold leading-tight text-[#4B4B4B] sm:text-[30px] md:text-[34px] lg:text-[36px]">
            Bring your business online with Chaafo.
          </h2>

          <p className="mt-3 text-[14px] leading-6 text-[#9AA8C0] sm:text-[15px] md:text-[17px] lg:text-[18px]">
            It takes less than 2 minutes to register and less than 48 hours to
            go live.
          </p>
        </div>

        {/* Right */}
        <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:flex-wrap sm:justify-center lg:justify-end">
          <button className="flex h-[52px] items-center justify-center gap-3 rounded-full bg-[#251A72] px-6 text-[15px] font-medium text-white transition hover:bg-[#1d145b] sm:h-14 sm:px-8 sm:text-[16px]">
            <ArrowUpRight size={18} />
            Register Now
          </button>

          <button className="flex h-[52px] items-center justify-center gap-3 rounded-full border border-gray-200 bg-white px-6 text-[15px] font-medium text-[#4B4B4B] transition hover:bg-gray-50 sm:h-14 sm:px-8 sm:text-[16px]">
            <ArrowUpRight size={18} />
            Talk to Sales
          </button>
        </div>
      </div>
    </section>
  );
}

export default function AboutPage() {
  return (
    <>
      <Navbar />
      <DownloadApp />
      <WhatWeDo />
      <OurDirection />
      <StatsSection />
      <MissionVision />
      <BuiltForFood />
      <PartnerSection />
      <Footer />
    </>
  );
}
